import { useState } from "react";
import { actions } from "../store";
import { STAGE_ORDER, type JobCardModel, type PipelineStage } from "../types";
import { ScoreRing } from "./ScoreRing";

function nextStage(stage: PipelineStage, dir: 1 | -1): PipelineStage | null {
  const i = STAGE_ORDER.indexOf(stage) + dir;
  return STAGE_ORDER[i] ?? null;
}

export function JobCard({ job }: { job: JobCardModel }) {
  const [matching, setMatching] = useState(false);
  const prev = nextStage(job.stage, -1);
  const next = nextStage(job.stage, 1);

  async function runMatch() {
    setMatching(true);
    try {
      await actions.match({ jobId: job.id }, "user");
    } catch {
      /* error is surfaced in the activity feed */
    } finally {
      setMatching(false);
    }
  }

  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", job.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="group animate-slide-in rounded-xl border border-border bg-surface-2/90
        p-3.5 transition-colors hover:border-border-bright"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="truncate font-display text-[15px] font-medium leading-snug text-text">
            {job.title}
          </h4>
          <p className="mt-0.5 truncate text-xs text-muted">
            {job.company}
            {job.department && job.department !== "Not specified" && (
              <span className="text-faint"> · {job.department}</span>
            )}
          </p>
        </div>
        {job.match ? (
          <ScoreRing score={job.match.score} />
        ) : (
          <span className="shrink-0 rounded-md border border-border px-1.5 py-0.5 font-mono text-[10px] text-faint">
            unscored
          </span>
        )}
      </div>

      <div className="mt-2.5 flex items-center gap-2 text-[11px] text-faint">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
          <path
            d="M12 21s7-5.686 7-11a7 7 0 10-14 0c0 5.314 7 11 7 11z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        </svg>
        <span className="min-w-0 truncate">{job.location}</span>
        {/* Provenance seal — this role came from the company's own portal. */}
        <span className="seal ml-auto shrink-0" title="Sourced from the company's official career portal">
          <svg width="8" height="8" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 1.4l2.1 8.5 8.5 2.1-8.5 2.1L12 22.6l-2.1-8.5L1.4 12l8.5-2.1z"
              fill="currentColor"
            />
          </svg>
          official
        </span>
        {job.seniority && (
          <span className="shrink-0 rounded border border-border-bright px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted">
            {job.seniority}
          </span>
        )}
      </div>

      {job.description && (
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted">
          {job.description}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <a
          href={job.officialUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 font-mono text-[11px] text-accent
            hover:text-text hover:underline"
        >
          view posting
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 17L17 7M17 7H9M17 7v8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>

        <div className="flex items-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
          <button
            onClick={runMatch}
            disabled={matching}
            className="rounded-md border border-border px-2 py-1 text-[11px] text-muted
              transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
            title="Score this role against your resume"
          >
            {matching ? "matching…" : job.match ? "re-match" : "match"}
          </button>
          <button
            onClick={() => actions.removeJob(job.id)}
            className="rounded-md border border-transparent p-1 text-faint transition-colors
              hover:border-rose/40 hover:text-rose"
            title="Remove from board"
            aria-label="Remove from board"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between border-t border-border/60 pt-2">
        <button
          onClick={() => prev && actions.moveJob(job.id, prev)}
          disabled={!prev}
          className="text-[11px] text-faint transition-colors hover:text-text disabled:invisible"
        >
          ← {prev && STAGE_ORDER.includes(prev) ? "back" : ""}
        </button>
        <button
          onClick={() => next && actions.moveJob(job.id, next)}
          disabled={!next}
          className="text-[11px] text-faint transition-colors hover:text-text disabled:invisible"
        >
          advance →
        </button>
      </div>
    </article>
  );
}
